import { AgGridReact } from "ag-grid-react";
import { ColDef, themeQuartz } from "ag-grid-community";
import { useMemo } from "react";
import { Currency } from "@/gql/graphql";
import dayjs from "dayjs";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { ReportInvoiceListItem } from "./GraphQL/reportInvoicesQuery";
import { useTranslations } from "use-intl";

const paymentTheme = themeQuartz.withParams({
  browserColorScheme: "light",
  fontFamily: {
    googleFont: "Inter",
  },
  fontSize: 11,
  headerFontSize: 12,
  spacing: 3,
});

interface InvoicePaymentDetailsProps {
  invoice: ReportInvoiceListItem;
}

export function InvoicePaymentDetails({ invoice }: InvoicePaymentDetailsProps) {
  const t = useTranslations("payment");
  const payments = invoice.payments || [];

  const columns = useMemo(() => {
    return [
      {
        field: "payment_reference" as any,
        headerName: "Payment Reference",
        flex: 1,
      },
      {
        field: "payment_date" as any,
        headerName: "Date",
        flex: 1,
        valueFormatter: (params: any) => {
          if (!params.value) return "";
          return dayjs(params.value).format("DD/MM/YYYY");
        },
      },
      {
        field: "payment_method" as any,
        headerName: "Method",
        flex: 1,
        valueFormatter: (params: any) =>
          params.value ? t(`method.${params.value}`) : null,
      },
      {
        field: "amount" as any,
        headerName: "Amount",
        flex: 1,
        valueFormatter: (params: any) => {
          return formatCurrency(params.value, invoice.currency as Currency, {
            emptyValue: "",
          });
        },
      },
      {
        field: "payment_description" as any,
        headerName: "Description",
        flex: 2,
      },
    ] as ColDef[];
  }, [invoice.currency]);

  return (
    <div className="bg-gray-50 p-4">
      {payments.length === 0 ? (
        <div className="text-center text-gray-500">
          No payments recorded for this invoice
        </div>
      ) : (
        <>
          <h4 className="mb-3 text-sm font-medium text-gray-900">
            Payment History ({payments.length} payment
            {payments.length !== 1 ? "s" : ""})
          </h4>
          <div className="h-48">
            <AgGridReact
              rowData={payments}
              columnDefs={columns}
              theme={paymentTheme}
              suppressMovableColumns={true}
              suppressColumnVirtualisation={true}
              headerHeight={32}
              rowHeight={28}
            />
          </div>
        </>
      )}
    </div>
  );
}
